import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import * as db from '@/lib/data-service';
import ProfileCard from '@/components/ProfileCard';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Settings, Clock, Check, Pencil, ShieldCheck, Star, X, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function Profile() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [applying, setApplying] = useState(false);
  const [jjAvailable, setJjAvailable] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    db.canUseJaiJinendra(user.id).then(setJjAvailable);
  }, [user?.id]);

  if (!profile) return null;
  const photos = db.getPhotos(user.id);
  const profileWithPhotos = { ...profile, photos };

  const vouchStatus = profile.vouch_status || 'none';

  const handleApplyVerification = async () => {
    setApplying(true);
    const ok = await db.applyForVerification(user.id);
    setApplying(false);
    if (ok) {
      toast('Vouch request sent! Community members in your city can now vouch for you.');
      if (refreshProfile) refreshProfile();
    } else {
      toast('Could not send — you may have already requested recently.');
    }
  };

  return (
    <div className="h-full overflow-auto bg-background" data-testid="profile-screen">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <h1 className="text-xl font-bold text-foreground">My Profile</h1>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => navigate('/edit-profile')} data-testid="edit-profile-btn">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} data-testid="settings-btn">
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Profile card */}
      <div className="px-3 pb-3">
        <ProfileCard profile={profileWithPhotos} isOwnProfile={true} />
      </div>

      {/* Info section */}
      <div className="px-4 space-y-3 pb-8">

        {/* Verification status card */}
        <div className="rounded-[20px] overflow-hidden border border-[#F0E3EC] bg-white" data-testid="verification-card">

          {/* Selfie verification row */}
          {profile.selfie_submitted_at && (
            <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-[#F5EEF3]">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${profile.selfie_verified ? 'bg-[#E9F9EF]' : 'bg-[#FEF9E7]'}`}>
                {profile.selfie_verified
                  ? <ShieldCheck className="h-5 w-5 text-[#27AE60]" />
                  : <Clock className="h-5 w-5 text-[#E67E22]" />
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#1A1814]">Selfie verification</p>
                <p className="text-xs text-[#A8A5A0] mt-0.5">
                  {profile.selfie_verified
                    ? 'Approved · your identity is confirmed'
                    : 'In review · usually approved within 24 hrs'
                  }
                </p>
              </div>
            </div>
          )}

          {/* Community status */}
          <div className="px-5 py-5">
            <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#C857A8] mb-4">Community status</p>

            {vouchStatus === 'none' && !profile.selfie_submitted_at && (
              <p className="text-sm text-[#A8A5A0] leading-relaxed">Complete your profile to become eligible for community vouching.</p>
            )}

            {vouchStatus === 'none' && profile.selfie_submitted_at && !profile.selfie_verified && (
              <p className="text-sm text-[#A8A5A0] leading-relaxed">Selfie under review. Vouching opens once your identity is verified.</p>
            )}

            {vouchStatus === 'member' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#EDE0EA] flex items-center justify-center shrink-0">
                    <span className="text-[#8A3976] text-sm font-bold">M</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#1A1814]">Member</p>
                    <p className="text-xs text-[#A8A5A0] mt-0.5">Community members in your city can vouch for you</p>
                  </div>
                </div>
                <Button
                  onClick={handleApplyVerification}
                  disabled={applying || profile.applied_for_verification}
                  className="w-full h-11 rounded-full bg-[#C857A8] hover:bg-[#B54E9A] text-white text-sm font-semibold disabled:opacity-70"
                  data-testid="apply-verification-btn"
                >
                  {applying ? 'Sending...' : profile.applied_for_verification ? 'Vouch request sent ✓' : 'Request vouches'}
                </Button>
              </div>
            )}

            {vouchStatus === 'vouched' && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#8A3976] flex items-center justify-center shrink-0">
                  <Check className="h-5 w-5 text-white" strokeWidth={3} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1A1814]">Vouched</p>
                  <p className="text-xs text-[#A8A5A0] mt-0.5">Trusted by the Jiv community</p>
                </div>
              </div>
            )}

            {vouchStatus === 'verified' && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg, #C857A8, #6F2A62)' }}>
                  <Check className="h-5 w-5 text-white" strokeWidth={3} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1A1814]">Jiv Verified</p>
                  <p className="text-xs text-[#A8A5A0] mt-0.5">Founding member</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Jai Jinendra status */}
        <div className="flex items-center gap-3 px-5 py-4 rounded-[20px] border border-[#F0E3EC] bg-white">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${jjAvailable ? 'bg-[#C857A8]' : 'bg-[#F0EDE9]'}`}>
            <Star className={`h-5 w-5 ${jjAvailable ? 'text-white fill-white' : 'text-[#C8C3BE]'}`} />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#1A1814]">Jai Jinendra</p>
            <p className="text-xs text-[#A8A5A0] mt-0.5" data-testid="jai-jinendra-status">
              {jjAvailable ? 'Available this week · use it for someone who stands out' : 'Resets Monday'}
            </p>
          </div>
        </div>

        {/* Community Vouching — verified users only */}
        {vouchStatus === 'verified' && (
          <VouchReviewSection userId={user.id} />
        )}

      </div>
    </div>
  );
}

function VouchReviewSection({ userId }) {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthlyCount, setMonthlyCount] = useState(0);
  const [selectedApplicant, setSelectedApplicant] = useState(null);

  useEffect(() => {
    Promise.all([
      db.getVouchApplicants(userId),
      db.getMonthlyVouchCount(userId),
    ]).then(([apps, count]) => {
      setApplicants(apps || []);
      setMonthlyCount(count || 0);
      setLoading(false);
    });
  }, [userId]);

  const handleVouch = async (applicantId) => {
    const ok = await db.createVouch(userId, applicantId);
    if (ok) {
      setApplicants(prev => prev.filter(a => a.id !== applicantId));
      setMonthlyCount(prev => prev + 1);
      setSelectedApplicant(null);
      toast('Vouch sent!');
    } else {
      toast('Could not send vouch — you may not be eligible or already vouched for them.');
    }
  };

  const handleSkip = (applicantId) => {
    setApplicants(prev => prev.filter(a => a.id !== applicantId));
    setSelectedApplicant(null);
  };

  return (
    <>
      <div className="rounded-[20px] border border-[#F0E3EC] bg-white overflow-hidden" data-testid="vouch-review-section">
        <div className="flex items-center gap-3 px-5 pt-5 pb-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg, #C857A8, #6F2A62)' }}>
            <Users className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#1A1814]">Community Vouching</p>
            <p className="text-xs text-[#A8A5A0] mt-0.5">
              {loading ? 'Loading...' : applicants.length === 0 ? 'No pending vouch requests' : `${applicants.length} request${applicants.length !== 1 ? 's' : ''} · ${monthlyCount} vouched this month`}
            </p>
          </div>
        </div>
        {!loading && applicants.length > 0 && (
          <div className="px-4 pb-4 space-y-2.5">
            {applicants.map(applicant => (
              <VouchCard
                key={applicant.id}
                applicant={applicant}
                onVouch={() => handleVouch(applicant.id)}
                onSkip={() => handleSkip(applicant.id)}
                onViewProfile={() => setSelectedApplicant(applicant)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Full profile popup */}
      <Dialog open={!!selectedApplicant} onOpenChange={() => setSelectedApplicant(null)}>
        <DialogContent className="max-w-sm rounded-xl p-0 overflow-hidden max-h-[88vh] flex flex-col">
          <div className="flex-1 overflow-y-auto">
            {selectedApplicant && <ProfileCard profile={selectedApplicant} />}
          </div>
          {selectedApplicant && (
            <div className="flex gap-3 px-4 py-4 border-t border-[#F0EDE9] shrink-0 bg-white">
              <button
                onClick={() => handleSkip(selectedApplicant.id)}
                className="flex-1 h-11 rounded-full border border-[#E0DDD9] text-sm font-medium text-[#7A7672] hover:bg-[#F0EDE9] transition-colors"
                data-testid="popup-skip-vouch"
              >
                Skip
              </button>
              <button
                onClick={() => handleVouch(selectedApplicant.id)}
                className="flex-1 h-11 rounded-full text-white text-sm font-semibold flex items-center justify-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #C857A8, #6F2A62)' }}
                data-testid="popup-confirm-vouch"
              >
                <Check className="h-4 w-4" strokeWidth={3} />
                Vouch
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function VouchCard({ applicant, onVouch, onSkip, onViewProfile }) {
  const photo = applicant.photos?.[0];
  return (
    <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#FAF8F5] border border-[#F0EDE9]" data-testid={`vouch-card-${applicant.id}`}>
      <button onClick={onViewProfile} className="flex items-center gap-3 flex-1 min-w-0 text-left">
        <div className="w-11 h-11 rounded-full overflow-hidden bg-[#F0EDE9] shrink-0">
          {photo
            ? <img src={photo.url} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-sm font-bold text-[#C8C3BE]">{applicant.display_name?.[0]}</div>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#1A1814] truncate">
            {applicant.display_name}{applicant.last_initial ? ` ${applicant.last_initial.charAt(0)}.` : ''}
          </p>
          <p className="text-xs text-[#A8A5A0] mt-0.5 truncate">
            {applicant.city}{applicant.sect ? ` · ${applicant.sect}` : ''}
          </p>
        </div>
      </button>
      <div className="flex gap-2 shrink-0">
        <button
          onClick={onSkip}
          className="h-9 w-9 rounded-full border border-[#E0DDD9] flex items-center justify-center hover:bg-[#F0EDE9] transition-colors"
          data-testid={`skip-vouch-${applicant.id}`}
        >
          <X className="h-3.5 w-3.5 text-[#A8A5A0]" />
        </button>
        <button
          onClick={onVouch}
          className="h-9 w-9 rounded-full flex items-center justify-center transition-colors"
          style={{ background: 'linear-gradient(135deg, #C857A8, #6F2A62)' }}
          data-testid={`confirm-vouch-${applicant.id}`}
        >
          <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}
